import type { CrmFieldSelectOption } from '@/api/types/crm/crm.types'

export const DEFAULT_CRM_FIELD_SELECT_OPTION_COLOR = '#4169E1'

export type CrmFieldSelectOptionInput =
  | string
  | Partial<CrmFieldSelectOption>
  | null
  | undefined

export type CrmFieldSelectOptionDraft = CrmFieldSelectOption & {
  id: string
}

const createSelectOptionDraftId = () =>
  `select-option-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

export const normalizeCrmFieldSelectOptionColor = (value: unknown): string => {
  const color = String(value ?? '').trim()
  return /^#[0-9a-f]{6}$/i.test(color)
    ? color
    : DEFAULT_CRM_FIELD_SELECT_OPTION_COLOR
}

export const normalizeCrmFieldSelectOption = (
  option: CrmFieldSelectOptionInput,
): CrmFieldSelectOption | null => {
  if (option == null) {
    return null
  }

  if (typeof option === 'string') {
    const label = option.trim()
    return label
      ? { label, color: DEFAULT_CRM_FIELD_SELECT_OPTION_COLOR }
      : null
  }

  const label = String(option.label ?? '').trim()
  if (!label) {
    return null
  }

  return {
    ...(option.id ? { id: option.id } : {}),
    label,
    color: normalizeCrmFieldSelectOptionColor(option.color),
  }
}

export const normalizeCrmFieldSelectOptions = (
  options?: Array<string | CrmFieldSelectOption> | null,
): CrmFieldSelectOption[] =>
  (options ?? [])
    .map(normalizeCrmFieldSelectOption)
    .filter((item): item is CrmFieldSelectOption => item != null)

export const serializeCrmFieldSelectOptionsForApi = (
  options: CrmFieldSelectOptionDraft[],
): Array<{ id: string; label: string; color: string }> =>
  options
    .filter((o) => o.label.trim())
    .map((o) => ({
      id: o.id,
      label: o.label.trim(),
      color: normalizeCrmFieldSelectOptionColor(o.color),
    }))

export const createEmptyCrmFieldSelectOptionDraft = (
  color: string = DEFAULT_CRM_FIELD_SELECT_OPTION_COLOR,
): CrmFieldSelectOptionDraft => ({
  id: createSelectOptionDraftId(),
  label: '',
  color: normalizeCrmFieldSelectOptionColor(color),
})

export const mapCrmFieldSelectOptionsToDrafts = (
  options?: Array<string | CrmFieldSelectOption> | null,
): CrmFieldSelectOptionDraft[] =>
  normalizeCrmFieldSelectOptions(options).map((option) => ({
    ...option,
    id: option.id || createSelectOptionDraftId(),
  }))
